import { useScrollCallback } from '@doltech/core/lib/hooks/useScrollCallback';
import { getScrollYOf } from '@doltech/core/lib/hooks/useScrollElement';
import { MotionValue, useMotionValue } from 'framer-motion';
import * as React from 'react';
import { useWindowSize } from 'react-use';

interface FollowElementProps {
  element: React.MutableRefObject<any>;
  elementToFollowRef: React.MutableRefObject<any>;
  gap?: number;
}

interface UpdatePositionByParams {
  scrollingDistance: number;
  initialPosition: number;
  stickyTop: MotionValue<number>;
  gap: number;
  currentElement: HTMLElement;
  elementToFollow: HTMLElement;
}

function updatePositionBy(params: UpdatePositionByParams) {
  const { scrollingDistance, initialPosition, stickyTop, gap, currentElement, elementToFollow } =
    params;
  if (scrollingDistance > initialPosition) {
    // use scrolled to the element
    if (
      elementToFollow?.getBoundingClientRect()?.height >
      scrollingDistance - initialPosition + gap
    ) {
      stickyTop.set(0);
    } else {
      stickyTop.set(-currentElement?.getBoundingClientRect()?.height);
    }
  } else {
    // user has not scrolled to element yet
    stickyTop.set(initialPosition - scrollingDistance);
  }
}

interface FollowElementContextProps {
  scrollContainer: React.RefObject<HTMLElement | Window>;
}

export const FollowElementContext = React.createContext<FollowElementContextProps>({
  scrollContainer: null,
});

export const useFollowElement = () => React.useContext(FollowElementContext);

export const useFollowElementVertically = ({
  element,
  elementToFollowRef,
  gap = 0,
}: FollowElementProps) => {
  const { scrollContainer } = useFollowElement();
  const { width } = useWindowSize();
  const [initialPosition, setInitialPosition] = React.useState(0);
  const currentElement = element?.current;
  const elementToFollow = elementToFollowRef?.current;
  const stickyTop = useMotionValue(0);
  const handleScroll = React.useCallback(() => {
    updatePositionBy({
      scrollingDistance: getScrollYOf(scrollContainer.current),
      initialPosition,
      stickyTop,
      gap,
      currentElement,
      elementToFollow,
    });
  }, [currentElement, elementToFollow, gap, initialPosition, scrollContainer, stickyTop]);

  // Re-calculate position after user scrolls
  useScrollCallback(scrollContainer, handleScroll);

  // Re-calculate position after initial position updated
  React.useEffect(() => {
    handleScroll();
  }, [handleScroll]);

  // Re-calculate initial position after window re-size width
  React.useEffect(() => {
    const initial = elementToFollowRef?.current?.offsetTop;
    setInitialPosition(initial);
    stickyTop.set(initial);
  }, [elementToFollowRef, stickyTop, width]);

  return { top: stickyTop };
};
